import React, { useState } from "react";
import { Switch, Typography } from "@mui/material";
import { Checkbox, DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { Inline, Stack } from "components/layout/reusableLayouts";

export type DeliveryMode = "email" | "sms" | "push";

export interface NotificationChannelRow {
  id: string;
  title: string;
  description: string;
  isEnable: boolean;
  deliveryModes: string[];
}

export interface NotificationSettingInput {
  isEnable?: boolean;
  deliveryModes?: string[];
}

interface NotificationChannelTableProps {
  /** Header of the setting column. */
  heading: string;
  /** Channel column headers; each caller keeps its own translation keys. */
  channelLabels: Record<DeliveryMode, string>;
  rows: NotificationChannelRow[];
  loading: boolean;
  /** Persists one change; the row stays disabled until it settles. */
  onSave: (id: string, input: NotificationSettingInput) => Promise<void>;
}

/** A switch per setting and a checkbox per delivery channel. */
const NotificationChannelTable: React.FC<NotificationChannelTableProps> = ({
  heading,
  channelLabels,
  rows,
  loading,
  onSave,
}) => {
  const { t } = useTranslation();
  const [updatingRows, setUpdatingRows] = useState<{ [id: string]: boolean }>(
    {},
  );

  const save = async (id: string, input: NotificationSettingInput) => {
    setUpdatingRows((prev) => ({ ...prev, [id]: true }));
    try {
      await onSave(id, input);
    } finally {
      setUpdatingRows((prev) => ({ ...prev, [id]: false }));
    }
  };

  const toggleDeliveryMode = (
    row: NotificationChannelRow,
    mode: DeliveryMode,
    checked: boolean,
  ) => {
    const deliveryModes = checked
      ? Array.from(new Set([...row.deliveryModes, mode]))
      : row.deliveryModes.filter((item) => item !== mode);
    save(row.id, { deliveryModes });
  };

  const channelColumn = (
    mode: DeliveryMode,
    label: string,
  ): DataColumn<NotificationChannelRow> => ({
    id: mode,
    header: label,
    align: "center",
    width: 124,
    cell: (row) => (
      <Checkbox
        name={mode}
        checked={row.deliveryModes.includes(mode)}
        onChange={(event) =>
          toggleDeliveryMode(row, mode, event.target.checked)
        }
        disabled={!row.isEnable || !!updatingRows[row.id]}
        slotProps={{ input: { "aria-label": `${row.title}: ${label}` } }}
      />
    ),
  });

  const columns: DataColumn<NotificationChannelRow>[] = [
    {
      id: "setting",
      header: heading,
      flex: true,
      cell: (row) => (
        <Inline wrap={false} sx={{ py: "var(--space-3)" }}>
          <Switch
            checked={row.isEnable}
            onChange={(event) =>
              save(row.id, { isEnable: event.target.checked })
            }
            disabled={!!updatingRows[row.id]}
            slotProps={{ input: { "aria-label": row.title } }}
          />
          {/* DataTable never shrinks below max-content, so the text reports no
              width of its own and wraps to the column instead of widening it. */}
          <Stack
            gap="var(--space-1)"
            sx={{ flex: 1, width: 0, minWidth: 0, whiteSpace: "normal" }}
          >
            <Typography variant="subtitle2">{row.title}</Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {row.description}
            </Typography>
          </Stack>
        </Inline>
      ),
    },
    channelColumn("email", channelLabels.email),
    channelColumn("sms", channelLabels.sms),
    channelColumn("push", channelLabels.push),
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      getRowKey={(row) => row.id}
      loading={loading}
      emptyState={t("NoResultsFound")}
      fitColumns
      stickyHeader
    />
  );
};

export default NotificationChannelTable;
